"use client";

import { useEffect, useRef, useState } from "react";

type ScannerState = "idle" | "starting" | "ready" | "redirecting" | "error";

const QR_CODE_PATTERN = /^\d{6}$/;
const GAME_BASE_URL = "https://casacazesp.tm1.com.br/play";
const EXPERIENCE_QR_HOSTS = new Set([
  "casacazetv.com.br",
  "www.casacazetv.com.br",
  "qrco.de",
  "www.qrco.de",
]);
const CAMERA_WARMUP_TIMEOUT_MS = 4000;
const EXPERIENCE_QR_PATH_PATTERN = /^\/filas\/experiencia(?:\/.*)?$/i;
const EXPERIENCE_QR_TEXT_PATTERN =
  /^(?:https?:\/\/)?(?:www\.)?casacazetv\.com\.br\/filas\/experiencia(?:\/[^\s]*)?(?:\?[^\s]*)?(?:#[^\s]*)?$/i;
const QRCO_DE_TEXT_PATTERN =
  /^(?:https?:\/\/)?(?:www\.)?qrco\.de(?:\/[^\s]*)?(?:\?[^\s]*)?(?:#[^\s]*)?$/i;

type ScannerControls = {
  stop: () => void;
};

type CameraStartResult = {
  controls: ScannerControls;
  stream: MediaStream;
};

function buildGameUrl(code: string) {
  const email = window.localStorage.getItem("cazeemail")?.trim() ?? "";
  const nickname = window.localStorage.getItem("cazename")?.trim() ?? "";
  const url = new URL(`${GAME_BASE_URL}/${code}`);

  url.searchParams.set("email", email);
  url.searchParams.set("apelido", nickname);

  return url.toString();
}

function resolveQrDestination(rawValue: string) {
  const rawTrimmedValue = rawValue.trim();
  const numericCodeMatch = rawTrimmedValue.match(/\d{6}/);
  const numericCode = numericCodeMatch?.[0] ?? "";

  if (
    EXPERIENCE_QR_TEXT_PATTERN.test(rawTrimmedValue) ||
    QRCO_DE_TEXT_PATTERN.test(rawTrimmedValue)
  ) {
    const href = /^https?:\/\//i.test(rawTrimmedValue)
      ? rawTrimmedValue
      : `https://${rawTrimmedValue}`;

    return {
      href,
      code: null,
    };
  }

  try {
    const url = new URL(rawTrimmedValue);
    const normalizedPath = url.pathname.replace(/\/+$/, "");

    if (
      EXPERIENCE_QR_HOSTS.has(url.hostname.toLowerCase()) &&
      EXPERIENCE_QR_PATH_PATTERN.test(normalizedPath)
    ) {
      return {
        href: url.toString(),
        code: null,
      };
    }
  } catch {
    // Ignore invalid URLs and continue with the numeric QR format.
  }

  if (!QR_CODE_PATTERN.test(numericCode)) {
    return null;
  }

  return {
    href: buildGameUrl(numericCode),
    code: numericCode,
  };
}

export function QrCodeScannerPageContent() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerControlsRef = useRef<ScannerControls | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const hasScannedRef = useRef(false);
  const [scannerState, setScannerState] = useState<ScannerState>("idle");
  const [message, setMessage] = useState("Aponte a câmera para o QR Code.");
  const [reloadKey, setReloadKey] = useState(0);
  const [showInvalidStateModal, setShowInvalidStateModal] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [manualCodeError, setManualCodeError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const cleanupCamera = () => {
      scannerControlsRef.current?.stop();
      scannerControlsRef.current = null;

      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      const videoElement = videoRef.current;

      if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
        videoElement.removeAttribute("src");
        videoElement.load();
      }
    };

    async function waitForVideoReadiness(videoElement: HTMLVideoElement) {
      if (videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        return;
      }

      await new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          cleanup();
          reject(new Error("A camera demorou para disponibilizar a imagem."));
        }, CAMERA_WARMUP_TIMEOUT_MS);

        const handleReady = () => {
          cleanup();
          resolve();
        };

        const handleError = () => {
          cleanup();
          reject(new Error("Nao foi possivel preparar o video da camera."));
        };

        const cleanup = () => {
          videoElement.removeEventListener("loadedmetadata", handleReady);
          videoElement.removeEventListener("canplay", handleReady);
          videoElement.removeEventListener("error", handleError);
          window.clearTimeout(timeoutId);
        };

        videoElement.addEventListener("loadedmetadata", handleReady, {
          once: true,
        });
        videoElement.addEventListener("canplay", handleReady, { once: true });
        videoElement.addEventListener("error", handleError, { once: true });
      });
    }

    async function getPreferredStream() {
      const mediaDevices = navigator.mediaDevices;

      if (!mediaDevices?.getUserMedia) {
        throw new Error("Camera API indisponivel neste navegador.");
      }

      const attempts: MediaStreamConstraints[] = [
        {
          audio: false,
          video: {
            facingMode: {
              ideal: "environment",
            },
            width: {
              ideal: 1280,
            },
            height: {
              ideal: 1280,
            },
          },
        },
        {
          audio: false,
          video: {
            facingMode: "environment",
          },
        },
        {
          audio: false,
          video: true,
        },
      ];

      let lastError: unknown = null;

      for (const constraints of attempts) {
        try {
          return await mediaDevices.getUserMedia(constraints);
        } catch (error) {
          lastError = error;
        }
      }

      if (mediaDevices.enumerateDevices) {
        try {
          const devices = await mediaDevices.enumerateDevices();
          const environmentCamera = devices.find(
            (device) =>
              device.kind === "videoinput" &&
              /(back|rear|traseira|environment)/i.test(device.label),
          );

          if (environmentCamera?.deviceId) {
            return await mediaDevices.getUserMedia({
              audio: false,
              video: {
                deviceId: {
                  exact: environmentCamera.deviceId,
                },
              },
            });
          }
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError instanceof Error
        ? lastError
        : new Error("Nao foi possivel acessar a camera.");
    }

    async function startCamera(
      videoElement: HTMLVideoElement,
    ): Promise<CameraStartResult> {
      const stream = await getPreferredStream();

      videoElement.setAttribute("autoplay", "true");
      videoElement.setAttribute("muted", "true");
      videoElement.setAttribute("playsinline", "true");
      videoElement.setAttribute("webkit-playsinline", "true");
      videoElement.muted = true;
      videoElement.defaultMuted = true;
      videoElement.playsInline = true;
      videoElement.autoplay = true;
      videoElement.srcObject = stream;

      await waitForVideoReadiness(videoElement);
      await videoElement.play();

      const { BrowserQRCodeReader } = await import("@zxing/browser");
      const reader = new BrowserQRCodeReader();
      const controls = await reader.decodeFromVideoElement(
        videoElement,
        (result, error) => {
          if (!isMounted || hasScannedRef.current) {
            return;
          }

          if (result) {
            const destination = resolveQrDestination(result.getText());

            if (!destination) {
              hasScannedRef.current = true;
              setScannerState("error");
              setMessage("Este QR Code nao esta em um formato valido.");
              setShowInvalidStateModal(true);
              cleanupCamera();
              return;
            }

            hasScannedRef.current = true;
            setScannerState("redirecting");
            setMessage("QR Code lido com sucesso. Redirecionando...");
            cleanupCamera();
            window.location.assign(destination.href);
            return;
          }

          if (error) {
            const errorName =
              error instanceof Error ? error.name : String(error);

            if (
              errorName === "NotFoundException" ||
              errorName === "ChecksumException" ||
              errorName === "FormatException"
            ) {
              return;
            }

            setScannerState("error");
            setMessage(
              "Nao foi possivel ler o QR Code agora. Confira a iluminacao e tente novamente.",
            );
          }
        },
      );

      return {
        controls,
        stream,
      };
    }

    async function startScanner() {
      const videoElement = videoRef.current;

      if (!videoElement) {
        return;
      }

      cleanupCamera();
      setScannerState("starting");
      setMessage("Solicitando acesso à câmera...");
      hasScannedRef.current = false;

      try {
        const { controls, stream } = await startCamera(videoElement);

        if (!isMounted) {
          controls.stop();
          stream.getTracks().forEach((track) => track.stop());
          videoElement.pause();
          videoElement.srcObject = null;
          return;
        }

        scannerControlsRef.current = controls;
        streamRef.current = stream;
        setScannerState("ready");
        setMessage("Aponte a câmera para o QR Code.");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        const errorMessage =
          error instanceof Error ? error.message.toLowerCase() : "";
        const deniedAccess =
          errorMessage.includes("permission") ||
          errorMessage.includes("notallowed") ||
          errorMessage.includes("denied");

        setScannerState("error");
        setMessage(
          deniedAccess
            ? "Precisamos da permissao da camera para ler o QR Code."
            : "Nao foi possivel iniciar a camera neste dispositivo.",
        );
      }
    }

    startScanner();

    return () => {
      isMounted = false;
      cleanupCamera();
    };
  }, [reloadKey]);

  function handleManualCodeSubmit() {
    const normalizedCode = manualCode.trim().replace(/\D/g, "").slice(0, 6);

    if (normalizedCode.length !== 6) {
      setManualCodeError("Digite um codigo com 6 digitos.");
      return;
    }

    scannerControlsRef.current?.stop();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    setManualCodeError("");
    setScannerState("redirecting");
    setMessage("Codigo validado. Redirecionando...");
    window.location.assign(buildGameUrl(normalizedCode));
  }

  return (
    <section className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 pb-[140px] pt-6 text-center">
      <div className="reveal-up flex w-full flex-col items-center rounded-[28px] border-4 border-[#FEC822] bg-white/8 p-5 shadow-[0_20px_80px_rgba(0,0,0,0.24)] backdrop-blur-sm md:p-8">
        <h1 className="max-w-[14ch] text-[24px] font-black leading-[0.95] text-[#FEC822] md:text-[44px]">
          Leia o QR Code
        </h1>

        <div className="mt-5 w-full rounded-[28px] bg-[#07135F] p-3 shadow-inner shadow-black/30">
          <div className="relative aspect-square overflow-hidden rounded-[22px] border-2 border-white/20 bg-black">
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              autoPlay
              muted
              playsInline
              disablePictureInPicture
              aria-label="Leitor de QR Code"
            />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="h-[68%] w-[68%] rounded-[24px] border-4 border-[#FEC822] shadow-[0_0_0_9999px_rgba(7,19,95,0.28)]" />
            </div>
          </div>
        </div>

        <p className="mt-5 max-w-[30ch] text-sm font-medium leading-snug text-white/88">
          {message}
        </p>

        {/*<div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => {
              setShowInvalidStateModal(false);
              setReloadKey((current) => current + 1);
            }}
            className="pill-button cursor-pointer"
            disabled={
              scannerState === "starting" || scannerState === "redirecting"
            }
          >
            Tentar novamente
          </button>
        </div>*/}

        <div className="hidden mt-8 w-full max-w-[360px] rounded-[24px] border border-white/15 bg-[#07135F]/70 p-4 text-left">
          <p className="text-center text-sm font-bold uppercase tracking-[0.08em] text-[#FEC822]">
            Digite o código
          </p>
          <p className="mt-2 text-center text-sm leading-snug text-white/82">
            Digite o codigo de 6 digitos e entre no jogo manualmente.
          </p>

          <label
            htmlFor="manual-game-code"
            className="mt-4 block text-xs font-semibold uppercase tracking-[0.08em] text-white/70"
          >
            Código
          </label>
          <input
            id="manual-game-code"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            maxLength={6}
            value={manualCode}
            onChange={(event) => {
              const nextValue = event.target.value
                .replace(/\D/g, "")
                .slice(0, 6);
              setManualCode(nextValue);
              setManualCodeError("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleManualCodeSubmit();
              }
            }}
            placeholder="000000"
            className="mt-2 w-full rounded-full border-2 border-white/15 bg-white px-4 py-3 text-center text-lg font-black tracking-[0.35em] text-[#07135F] outline-none transition focus:border-[#FEC822]"
            aria-describedby="manual-game-code-help manual-game-code-error"
          />

          <p
            id="manual-game-code-help"
            className="mt-2 text-center text-xs text-white/60"
          >
            Use apenas numeros, com 6 digitos.
          </p>

          {manualCodeError ? (
            <p
              id="manual-game-code-error"
              className="mt-2 text-center text-sm font-semibold text-[#FEC822]"
            >
              {manualCodeError}
            </p>
          ) : null}

          <button
            type="button"
            onClick={handleManualCodeSubmit}
            className="mt-4 w-full rounded-full bg-[#FEC822] px-4 py-3 text-sm font-extrabold uppercase text-[#07135F] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={
              scannerState === "starting" || scannerState === "redirecting"
            }
          >
            Ir para o jogo
          </button>
        </div>
      </div>

      {showInvalidStateModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="relative w-full max-w-[300px] rounded-[14px] bg-[#fec822] px-5 pb-7 pt-9">
            <button
              type="button"
              onClick={() => {
                setShowInvalidStateModal(false);
                setReloadKey((current) => current + 1);
              }}
              className="absolute -right-[10px] -top-[12px] flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#2f43ef] text-white"
              aria-label="Fechar modal de aviso"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.7"
                className="h-5 w-5"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>

            <h2 className="text-center text-[18px] font-extrabold uppercase leading-none text-[#2033ef]">
              QR Code inválido
            </h2>
            <p className="mt-5 text-center text-[14px] leading-[1.15] text-black">
              Verifique se o QR Code contem uma URL da experiencia ou um codigo
              numerico de 6 digitos.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowInvalidStateModal(false);
                setReloadKey((current) => current + 1);
              }}
              className="mt-6 w-full rounded-full bg-[#2033ef] px-4 py-3 text-[14px] font-extrabold uppercase text-white"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
